import { type PropsWithChildren } from 'react';
import { FormattedMessage } from 'react-intl';

import {
  ArrowLeftIcon,
  ArrowRightIcon,
  CopyIcon,
  ViewOffIcon,
  WrapTextIcon,
} from '@dynatrace/strato-icons';

import { useCopyToClipboard } from '../../../../core/utils/useCopyToClipboard.js';
import { Group as MenuGroup } from '../../../../navigation/menu/Group.js';
import { Intent as MenuIntent } from '../../../../navigation/menu/Intent.js';
import { Item as MenuItem } from '../../../../navigation/menu/Item.js';
import { ItemIcon as MenuItemIcon } from '../../../../navigation/menu/ItemIcon.js';
import { Label as MenuLabel } from '../../../../navigation/menu/Label.js';
import { Link as MenuLink } from '../../../../navigation/menu/Link.js';
import { Sub as MenuSub } from '../../../../navigation/menu/Sub.js';
import { SubContent as MenuSubContent } from '../../../../navigation/menu/SubContent.js';
import { SubTrigger as MenuSubTrigger } from '../../../../navigation/menu/SubTrigger.js';
import { dataTableMessages } from '../../../DataTableV2/messages.js';
import {
  useDataTableV2,
  useDataTableV2Column,
} from '../../contexts/DataTableV2Context.js';

//# region Builtin actions

/**
 * Defines the props on the `CopyItem` table actions menu for copying the cell content to clipboard.
 * @public
 */
export type DataTableV2ActionsMenuCopyItemProps = {
  /**
   * The string representation of the cell value that gets copied to the clipboard when the user selects the action item.
   */
  value: string;
};

/**
 * Out of the box cell action for copying the cell content to clipboard.
 * This component can only be used as a child element of `<TableActionsMenu>`.
 */
export const CopyItem = ({ value }: DataTableV2ActionsMenuCopyItemProps) => {
  const [copyToClipboard] = useCopyToClipboard(value);
  return (
    <DataTableV2ActionsMenu.Item onSelect={copyToClipboard}>
      <DataTableV2ActionsMenu.ItemIcon>
        <CopyIcon />
      </DataTableV2ActionsMenu.ItemIcon>
      <FormattedMessage
        defaultMessage="Copy value"
        id="47orYkGnlAr7+sPw"
        description="Text in the cell action item for copying the content of the table cell to the clipboard."
      />
    </DataTableV2ActionsMenu.Item>
  );
};

/**
 * Out of the box line wrap toggle for header and cell actions.
 * @internal
 */
export function LineWrapAction() {
  const column = useDataTableV2Column();

  return (
    <DataTableV2ActionsMenu.Item onSelect={() => column.toggleLineWrap()}>
      <DataTableV2ActionsMenu.ItemIcon>
        <WrapTextIcon />
      </DataTableV2ActionsMenu.ItemIcon>
      {column?.getIsLineWrapped() ? (
        <FormattedMessage {...dataTableMessages.disableLineWrapLabel} />
      ) : (
        <FormattedMessage {...dataTableMessages.enableLineWrapLabel} />
      )}
    </DataTableV2ActionsMenu.Item>
  );
}

/**
 * Out of the box  wrap toggle for header and cell actions.
 * @internal
 */
export function HideColumnAction() {
  const table = useDataTableV2();
  const currentVisibility = table.getState().columnVisibility;
  const column = useDataTableV2Column();
  const childColumns = column.columns;

  const handleSelect = () => {
    if (childColumns.length === 0) {
      column.toggleVisibility();
    } else {
      const updatedChildrenVisibility = childColumns.reduce(
        (updatedVisibility: Record<string, boolean>, childColumn) => {
          return {
            ...updatedVisibility,
            [childColumn.id]: false,
          };
        },
        {},
      );
      table.setColumnVisibility({
        ...currentVisibility,
        ...updatedChildrenVisibility,
      });
    }
  };

  return (
    <DataTableV2ActionsMenu.Item
      onSelect={handleSelect}
      disabled={!column.getCanHide()}
    >
      <DataTableV2ActionsMenu.ItemIcon>
        <ViewOffIcon />
      </DataTableV2ActionsMenu.ItemIcon>
      <FormattedMessage {...dataTableMessages.hideColumnLabel} />
    </DataTableV2ActionsMenu.Item>
  );
}

/**
 * Out of the box column action for column order.
 * @internal
 */
export function ColumnOrderAction() {
  const column = useDataTableV2Column();
  const table = useDataTableV2();
  if (!table.options.enableColumnOrdering) {
    return null;
  }

  return (
    <>
      <DataTableV2ActionsMenu.Item
        onSelect={() => column.moveColumnLeft()}
        disabled={!column.getCanMoveColumnLeft()}
      >
        <DataTableV2ActionsMenu.ItemIcon>
          <ArrowLeftIcon />
        </DataTableV2ActionsMenu.ItemIcon>
        <FormattedMessage {...dataTableMessages.moveColumnLeftLabel} />
      </DataTableV2ActionsMenu.Item>
      <DataTableV2ActionsMenu.Item
        onSelect={() => column.moveColumnRight()}
        disabled={!column.getCanMoveColumnRight()}
      >
        <DataTableV2ActionsMenu.ItemIcon>
          <ArrowRightIcon />
        </DataTableV2ActionsMenu.ItemIcon>
        <FormattedMessage {...dataTableMessages.moveColumnRightLabel} />
      </DataTableV2ActionsMenu.Item>
    </>
  );
}

/**
 * Out of the box font style toggle for header and cell actions.
 * @internal
 */
export function ColumnFontStyleAction() {
  const column = useDataTableV2Column();

  return (
    <DataTableV2ActionsMenu.Item onSelect={() => column.toggleFontStyle()}>
      <DataTableV2ActionsMenu.ItemIcon>
        <WrapTextIcon />
      </DataTableV2ActionsMenu.ItemIcon>
      {column.getFontStyle() === 'text' ? (
        <FormattedMessage {...dataTableMessages.fontStyleCodeLabel} />
      ) : (
        <FormattedMessage {...dataTableMessages.fontStyleTextLabel} />
      )}
    </DataTableV2ActionsMenu.Item>
  );
}

//# endregion Builtin actions

/**
 * Slot components that can be used to configure the user actions menu.
 * @public
 */
export const DataTableV2ActionsMenu = Object.assign(
  function DataTableV2ActionsMenuInternal(props: PropsWithChildren) {
    // eslint-disable-next-line react/jsx-no-useless-fragment
    return <>{props.children}</>;
  },
  {
    Intent: MenuIntent,
    Item: MenuItem,
    ItemIcon: MenuItemIcon,
    Group: MenuGroup,
    Label: MenuLabel,
    CopyItem: CopyItem,
    SubMenu: MenuSub,
    SubContent: MenuSubContent,
    SubTrigger: MenuSubTrigger,
    Link: MenuLink,
    LineWrap: () => <LineWrapAction />,
    HideColumn: () => <HideColumnAction />,
    ColumnOrder: () => <ColumnOrderAction />,
    ColumnFontStyle: () => <ColumnFontStyleAction />,
  },
);
